import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Button from '../Shared/Button';
import { faChevronLeft, faChevronRight, faDownload } from '@fortawesome/free-solid-svg-icons';
import { downloadSettlementProfile, downloadSettlementProfileAsCSV } from '../../utils/settlementProfiles';
import { getAllSurveySettlements, SettlementDto } from '../../services/surveySettlement.service';
import { BREAKPOINTS, COLORS_BASE } from '../../utils/consts';
import { SubTitleStyle, TitleStyle } from '../../styles/styles';
import SectionContainer from '../Shared/SectionContainer';

type SettlementProfilesCarouselProps = {
  onViewOnMap: (id: number) => void;
};

function SettlementProfilesCarousel({ onViewOnMap }: SettlementProfilesCarouselProps) {
  const [settlements, setSettlements] = useState<SettlementDto[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const showcaseSettlements = settlements.slice(0, 3);
  const maxStartIndex = Math.max(0, showcaseSettlements.length - visibleCards);

  useEffect(() => {
    getAllSurveySettlements().then((data) => {
      if (Array.isArray(data)) setSettlements(data);
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateVisibleCards = () => {
      if (window.innerWidth <= 768) {
        setVisibleCards(1);
      } else if (window.innerWidth <= 1024) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };

    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);

    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, maxStartIndex));
  }, [maxStartIndex]);

  const goToPrev = () => {
    setActiveIndex((prev) => (prev === 0 ? maxStartIndex : prev - 1));
  };

  const goToNext = () => {
    setActiveIndex((prev) => (prev === maxStartIndex ? 0 : prev + 1));
  };

  if (showcaseSettlements.length === 0) return null;

  return (
    <SectionContainer className='fade-in' backgroundColor={COLORS_BASE.GREEN.MAIN} flex padding='0rem' margin='0' justifyContent='center'>
      <SectionContent>
        <TitleStyle>Settlement Energy Profiles</TitleStyle>
        <SubTitleStyle>
          Explore three sample village survey sites from the Department of Energy to preview how village-level evidence will support electrification planning.
        </SubTitleStyle>

        <CarouselContainer>
          {maxStartIndex > 0 && (
            <>
              <NavControl>
                <Button icon={faChevronLeft} filledStyle={false} width='15px' height='15px' onClick={goToPrev} />
              </NavControl>
              <NavControl right>
                <Button icon={faChevronRight} filledStyle={false} width='15px' height='15px' onClick={goToNext} />
              </NavControl>
            </>
          )}

          <Viewport>
            <Track style={{ transform: `translateX(-${activeIndex * (100 / visibleCards)}%)` }}>
              {showcaseSettlements.map((profile) => (
                <Slide key={profile.id}>
                  <ProfileCard>
                    <ProfileHeader>
                      <h3>{profile.settlement_identity.village_name}</h3>
                      <ProfileMeta>{profile.settlement_identity.district}</ProfileMeta>
                    </ProfileHeader>

                    <ProfileInfo>
                      <span>Total Households</span>
                      <strong>{profile.basic_scale.total_households ?? '—'}</strong>
                    </ProfileInfo>
                    <ProfileInfo>
                      <span>Distance to Grid</span>
                      <strong>{profile.basic_scale.distance_to_grid_km != null ? `${profile.basic_scale.distance_to_grid_km} km` : '—'}</strong>
                    </ProfileInfo>
                    <ProfileInfo>
                      <span>DOE Village Tag</span>
                      <strong>{profile.nes_planning_classification.display_label ?? profile.nes_planning_classification.nes_tag ?? '—'}</strong>
                    </ProfileInfo>

                    {(profile.storytelling_fields.headline_tag || profile.basic_scale.template_type) && (
                      <OpportunityTag>{profile.storytelling_fields.headline_tag ?? profile.basic_scale.template_type}</OpportunityTag>
                    )}

                    <CardActions>
                      <Button filledStyle={true} onClick={() => onViewOnMap(profile.id)}>
                        View on Map
                      </Button>
                      <Button icon={faDownload} filledStyle={true} onClick={() => downloadSettlementProfileAsCSV(profile)}>
                        Download Profile
                      </Button>
                    </CardActions>
                  </ProfileCard>
                </Slide>
              ))}
            </Track>
          </Viewport>

          {maxStartIndex > 0 && (
            <IndicatorRow>
              {Array.from({ length: maxStartIndex + 1 }).map((_, index) => (
                <Indicator
                  key={index}
                  active={index === activeIndex}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Go to carousel position ${index + 1}`}
                />
              ))}
            </IndicatorRow>
          )}
        </CarouselContainer>

        <ConfidenceNote>
          These three sites are illustrative previews. Additional results from 50+ DOE village surveys will be integrated in upcoming releases.
        </ConfidenceNote>
      </SectionContent>
    </SectionContainer>
  );
}

export default SettlementProfilesCarousel;

const SectionContent = styled.div<{}>(() => `
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: inherit;
  padding: 0;
  text-align: center;
`);

const CarouselContainer = styled.div<{}>(() => `
  position: relative;
  width: min(900px, 95%);
  margin-top: 20px;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    width: 100%;
  }
`);

const NavControl = styled.div<{ right?: boolean }>(({ right = false }) => `
  position: absolute;
  top: 50%;
  ${right ? 'right: 6px;' : 'left: 6px;'}
  transform: translateY(-50%);
  z-index: 3;
  button {
    padding: 0;
    margin: 0;
    border: none;
    box-shadow: 0 2px 6px rgba(255, 131, 78, 0.35);
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    top: auto;
    bottom: 8px;
    ${right ? 'right: 54px;' : 'left: 12px;'}
    transform: none;
  }
`);

const Viewport = styled.div<{}>(() => `
  overflow: hidden;
  border-radius: 10px;
`);

const Track = styled.div<{}>(() => `
  display: flex;
  transition: transform 0.35s ease;
`);

const Slide = styled.div<{}>(() => `
  min-width: calc(100% / 3);
  padding: 4px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    min-width: calc(100% / 2);
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    min-width: 100%;
  }
`);

const ProfileCard = styled.div<{}>(() => `
  position: relative;
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  border-top: 4px solid ${COLORS_BASE.SECONDARY.MAIN};
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-top-width 0.2s ease;

  h3 {
    margin: 0;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
    border-top-width: 8px;
  }
`);

const ProfileHeader = styled.div<{}>(() => `
  padding: 12px 0 14px 0;
  text-align: left;

  h3 {
    margin: 0 0 4px 0;
    color: ${COLORS_BASE.BRAND.DARKER};
  }
`);

const ProfileMeta = styled.p<{}>(() => `
  margin: 4px 0 0 0;
  color: ${COLORS_BASE.GREYS.DARKER};
  font-weight: 600;
`);

const ProfileInfo = styled.div<{}>(() => `
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`);

const OpportunityTag = styled.div<{}>(() => `
  margin-top: 12px;
  margin-bottom: 12px;
  display: inline-block;
  padding: 6px 10px;
  border-radius: 20px;
  background-color: ${COLORS_BASE.GREEN.LIGHT};
  font-size: 13px;
`);

const CardActions = styled.div<{}>(() => `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  button {
    width: 100%;
    min-height: 42px;
    justify-content: center;
    margin: 0;
    border: none;
    box-shadow: 0 2px 6px rgba(255, 131, 78, 0.35);
    font-weight: 600;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    grid-template-columns: 1fr;
  }
`);

const IndicatorRow = styled.div<{}>(() => `
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    padding-bottom: 44px;
  }
`);

const Indicator = styled.button<{ active: boolean }>(({ active }) => `
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background-color: ${active ? COLORS_BASE.SECONDARY.MAIN : '#ffffffa0'};
`);

const ConfidenceNote = styled.p<{}>(() => `
  margin-top: 24px;
  font-size: 14px;
`);
